/**
 * UserDropdown — A안 (`mockups/mypage-dropdown-A.html`) 사용자 드롭다운.
 *
 * GDEV-273 (238k). Navbar 에서 사용자 아바타 클릭 시 노출.
 * - 로그인 시: 프로필 / 내 데이터셋 / 내 큐레이션 / 내 모델 / 설정 / 로그아웃
 * - admin 추가: 회원 관리 / 모더레이션 / 모니터링
 */

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useApprovals } from '@/lib/api/admin'
import { useAuthStore } from '@/stores/authStore'
import {
  Building2,
  ChevronDown,
  ClipboardCheck,
  Database,
  FileText,
  LayoutGrid,
  LogOut,
  Package,
  Settings,
  Shield,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router'

interface UserDropdownProps {
  /** 컴팩트 모드: 아바타만 표시 */
  compact?: boolean
}

/**
 * 승인 대기 건수 뱃지 (H-18).
 *
 * 없으면 관리자가 화면을 열어야만 대기 건이 있는지 안다. 대기가 0 이면 아무것도
 * 그리지 않는다 — 평상시에 숫자가 붙어 있으면 눈이 무시하게 된다.
 */
function PendingApprovalCount() {
  const { data } = useApprovals({ status: 'pending', page: 1, size: 1 })
  const total = data?.total ?? 0
  if (total === 0) return null
  return (
    <span className="mono ml-auto rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-bold text-accent-fg">
      {total}
    </span>
  )
}

export function UserDropdown({ compact = false }: UserDropdownProps) {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()

  if (!user) return null

  const isAdmin = user.role === 'admin'
  const initial = (user.displayName ?? user.hfUsername ?? user.email ?? 'U')
    .slice(0, 1)
    .toUpperCase()

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="사용자 메뉴"
          // Navbar 배경(bg-nav-bg) 위에 놓이므로 text-foreground 가 아닌
          // nav 전용 토큰을 사용해야 light/dark 둘 다 가독성 확보.
          className="flex items-center gap-2 rounded-md border border-white/15 px-2 py-1 text-[12px] text-nav-text hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
        >
          <span
            aria-hidden="true"
            className="grid h-7 w-7 place-items-center rounded-full bg-white/15 text-[12px] font-bold text-nav-text"
          >
            {initial}
          </span>
          {!compact && (
            <span className="hidden max-w-[120px] truncate font-semibold text-nav-text sm:inline">
              {user.displayName ?? user.hfUsername ?? '사용자'}
            </span>
          )}
          <ChevronDown className="hidden h-3 w-3 text-nav-text-dim sm:block" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[240px]">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-[13px] font-semibold text-foreground">
            {user.displayName ?? user.hfUsername ?? '사용자'}
          </span>
          {user.email && (
            <span className="mono truncate text-[10px] font-normal text-muted-foreground">
              {user.email}
            </span>
          )}
          <span
            className={`mono mt-1 inline-flex w-fit rounded px-1.5 py-0.5 text-[10px] font-bold ${
              isAdmin ? 'bg-audit-soft text-audit-fg' : 'bg-muted text-muted-foreground'
            }`}
          >
            {user.role.toUpperCase()}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link to="/me" className="flex items-center gap-2 text-[12px]">
            <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />내 페이지
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/datasets?owner=me" className="flex items-center gap-2 text-[12px]">
            <Database className="h-3.5 w-3.5" aria-hidden="true" />내 데이터셋
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/curations?owner=me" className="flex items-center gap-2 text-[12px]">
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />내 큐레이션
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/models?owner=me" className="flex items-center gap-2 text-[12px]">
            <Package className="h-3.5 w-3.5" aria-hidden="true" />내 모델
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex items-center gap-2 text-[12px]">
            <Settings className="h-3.5 w-3.5" aria-hidden="true" />
            설정
          </Link>
        </DropdownMenuItem>

        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              🛡 Admin
            </DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to="/admin/users" className="flex items-center gap-2 text-[12px]">
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                회원 관리
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/approvals" className="flex items-center gap-2 text-[12px]">
                <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" />
                등록 승인
                <PendingApprovalCount />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/moderation" className="flex items-center gap-2 text-[12px]">
                <Shield className="h-3.5 w-3.5" aria-hidden="true" />
                모더레이션
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/monitoring" className="flex items-center gap-2 text-[12px]">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                모니터링
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/settings" className="flex items-center gap-2 text-[12px]">
                <Settings className="h-3.5 w-3.5" aria-hidden="true" />
                플랫폼 설정
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/hf-org" className="flex items-center gap-2 text-[12px]">
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                HF 조직
              </Link>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void handleLogout()
          }}
          className="flex items-center gap-2 text-[12px] text-destructive focus:text-destructive"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
          로그아웃
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
